import axios from 'axios';
import { isBuiltInLeadInsuranceType } from '@/views/RenovaDashboard/ProgramsParts/components/constants';

// Personal pipelines: a private board per team lead (and admin) inside Leads, served by
// /insurance/personal-pipeline/*. The backend decides who may open which pipeline; `access`
// is its answer and drives the switcher. Namespaced as `personalPipeline`.
//
// Every filter here is sent to the server — there is no client-side filtering, so the board,
// the counts and the export always agree.

export const defaultPersonalFilters = {
  search: '',
  clientTypes: [],
  insuranceCompanyIds: [],
  insuranceTypes: [],
  currencyCodes: [],
  paymentTypes: [],
  paymentStatuses: [],
  transactionStatuses: [],
  agentUserIds: [],
  agentCommissionPaymentStatuses: [],
  activity: [],
  tasks: [],
  createdFrom: null,
  createdTo: null,
  paymentDateFrom: null,
  paymentDateTo: null,
};

// Turns the UI's filter state (with its NOT_ASSIGNED / MISSING sentinels and mixed type ids)
// into the list endpoint's body.
export function toRequestFilters(filters) {
  const withoutSentinel = (values, sentinel) => (values || []).filter((v) => v !== sentinel);
  const types = withoutSentinel(filters.insuranceTypes, 'NOT_ASSIGNED');
  return {
    search: filters.search || null,
    clientTypes: filters.clientTypes,
    insuranceCompanyIds: withoutSentinel(filters.insuranceCompanyIds, 'NOT_ASSIGNED'),
    insuranceCompanyNotAssigned: (filters.insuranceCompanyIds || []).includes('NOT_ASSIGNED'),
    insuranceTypes: types.filter(isBuiltInLeadInsuranceType),
    customInsuranceTypeIds: types.filter((v) => !isBuiltInLeadInsuranceType(v)),
    insuranceTypeNotAssigned: (filters.insuranceTypes || []).includes('NOT_ASSIGNED'),
    currencyCodes: filters.currencyCodes,
    paymentTypes: filters.paymentTypes,
    paymentStatuses: filters.paymentStatuses,
    transactionStatuses: filters.transactionStatuses,
    agentUserIds: filters.agentUserIds,
    agentCommissionPaymentStatuses: withoutSentinel(filters.agentCommissionPaymentStatuses, 'MISSING'),
    withoutAgentCommissionPaymentStatus: (filters.agentCommissionPaymentStatuses || []).includes('MISSING'),
    activity: filters.activity,
    tasks: filters.tasks,
    createdFrom: filters.createdFrom,
    createdTo: filters.createdTo,
    paymentDateFrom: filters.paymentDateFrom,
    paymentDateTo: filters.paymentDateTo,
  };
}

// Cards per column on the first load and per "load more" page. deal/list pages a column in the
// same order deal/board returns it, so page N of a column is simply offset N * PAGE_SIZE.
export const PAGE_SIZE = 50;

const emptyColumns = () => ({});

const state = {
  access: {
    canUse: false, isAdmin: false, pipelines: [], loaded: false
  },
  // Owner of the pipeline on screen; null = the current user's own.
  ownerUserId: null,
  stages: [],
  customFields: [],
  // { [stageId]: { items, filteredCount, loadingMore } } — each column loads its own pages.
  columns: emptyColumns(),
  totalCount: 0,
  loading: false,
  filters: { ...defaultPersonalFilters },
};

const getters = {
  access: (s) => s.access,
  ownerUserId: (s) => s.ownerUserId,
  stages: (s) => s.stages,
  customFields: (s) => s.customFields,
  columns: (s) => s.columns,
  // Every loaded card, for the board (which groups them by stageId).
  deals: (s) => Object.values(s.columns).reduce((all, column) => all.concat(column.items), []),
  dealsTotalCount: (s) => s.totalCount,
  dealsLoading: (s) => s.loading,
  filters: (s) => s.filters,
};

const mutations = {
  SET_ACCESS(state, access) {
    state.access = { ...access, loaded: true };
  },
  SET_OWNER(state, ownerUserId) {
    state.ownerUserId = ownerUserId || null;
  },
  CLEAR_BOARD(state) {
    state.stages = [];
    state.customFields = [];
    state.columns = emptyColumns();
    state.totalCount = 0;
  },
  SET_BOARD(state, {
    items, totalCount, stages, customFields
  }) {
    const columns = {};
    stages.forEach((stage) => {
      columns[stage.id] = {
        items: items.filter((item) => item.stageId === stage.id),
        filteredCount: stage.filteredCount || 0,
        loadingMore: false,
      };
    });
    state.columns = columns;
    state.stages = stages;
    state.customFields = customFields;
    state.totalCount = totalCount;
  },
  // Stage list without counts (after a rename/reorder): keep each column's counts.
  SET_STAGES(state, stages) {
    state.stages = stages.map((stage) => ({
      ...stage,
      filteredCount: (state.columns[stage.id] || {}).filteredCount || 0,
    }));
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_COLUMN(state, { stageId, ...patch }) {
    const column = state.columns[stageId] || { items: [], filteredCount: 0, loadingMore: false };
    state.columns = { ...state.columns, [stageId]: { ...column, ...patch } };
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  RESET_FILTERS(state) {
    state.filters = { ...defaultPersonalFilters };
  },
};

// Owner switches, filter changes and refetches overlap. Every write to the board state captures the
// generation current when it started and is dropped if a newer board load has begun since, so a
// late answer can never mix another pipeline's (or another filter's) data into the screen.
let boardGeneration = 0;
const isCurrent = (generation) => generation === boardGeneration;

const post = (path, body) => axios.post(`/insurance/personal-pipeline/${path}`, body).then((r) => r.data);

const actions = {
  fetchAccess({ commit }) {
    return post('access', {})
      .then((access) => {
        commit('SET_ACCESS', access);
        return access;
      })
      .catch(() => {
        commit('SET_ACCESS', { canUse: false, isAdmin: false, pipelines: [] });
      });
  },
  setOwner({ commit, state: s }, ownerUserId) {
    if ((ownerUserId || null) === s.ownerUserId) return;
    boardGeneration += 1;
    commit('SET_OWNER', ownerUserId);
    commit('CLEAR_BOARD');
  },
  fetchBoard({ commit, state: s }) {
    boardGeneration += 1;
    const generation = boardGeneration;
    commit('SET_LOADING', true);
    return post('deal/board', {
      ownerUserId: s.ownerUserId,
      perStage: PAGE_SIZE,
      ...toRequestFilters(s.filters),
    })
      .then((data) => {
        if (isCurrent(generation)) commit('SET_BOARD', data);
        return data;
      })
      .finally(() => {
        if (isCurrent(generation)) commit('SET_LOADING', false);
      });
  },
  // The next page of one column. Pages are counted from what the column holds, and cards already
  // on screen (e.g. just dropped in) are not added twice.
  loadMoreStage({ commit, state: s }, stageId) {
    const column = s.columns[stageId];
    if (!column || column.loadingMore || column.items.length >= column.filteredCount) return Promise.resolve();
    const generation = boardGeneration;
    commit('SET_COLUMN', { stageId, loadingMore: true });
    return post('deal/list', {
      ownerUserId: s.ownerUserId,
      ...toRequestFilters(s.filters),
      stageIds: [stageId],
      page: Math.floor(column.items.length / PAGE_SIZE),
      limit: PAGE_SIZE,
    })
      .then((data) => {
        if (!isCurrent(generation)) return;
        const current = s.columns[stageId];
        const seen = new Set(current.items.map((item) => item.id));
        commit('SET_COLUMN', {
          stageId,
          items: current.items.concat(data.items.filter((item) => !seen.has(item.id))),
          filteredCount: data.totalCount,
        });
      })
      .finally(() => {
        if (isCurrent(generation)) commit('SET_COLUMN', { stageId, loadingMore: false });
      });
  },
  updateFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    return dispatch('fetchBoard');
  },
  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    return dispatch('fetchBoard');
  },

  // --- stages ---
  saveStage({ state: s, dispatch }, { id, name, color }) {
    return post('stage/save', {
      ownerUserId: s.ownerUserId, id, name, color
    })
      .then((stage) => dispatch('refreshStages').then(() => stage));
  },
  reorderStages({ state: s, commit }, stageIds) {
    const generation = boardGeneration;
    return post('stage/reorder', { ownerUserId: s.ownerUserId, stageIds })
      .then((data) => {
        if (isCurrent(generation)) commit('SET_STAGES', data.items);
      });
  },
  deleteStage({ state: s, dispatch }, { stageId, moveToStageId }) {
    return post('stage/delete', { ownerUserId: s.ownerUserId, stageId, moveToStageId })
      .then(() => dispatch('fetchBoard'));
  },
  refreshStages({ state: s, commit }) {
    const generation = boardGeneration;
    return post('stage/list', { ownerUserId: s.ownerUserId }).then((data) => {
      if (isCurrent(generation)) commit('SET_STAGES', data.items);
    });
  },

  // --- deals ---
  // `deal` follows PersonalDealSaveProc.Input: xSave / xDelete, dealId for an update.
  saveDeal({ state: s, dispatch }, deal) {
    return post('deal/save', { ownerUserId: s.ownerUserId, deal })
      .then((saved) => dispatch('fetchBoard').then(() => saved));
  },
  // A drop on the board. The card is shown at the drop point at once; the server places it for
  // real (from its actual neighbours) and the board is reloaded to match.
  moveDeal({ commit, dispatch, state: s }, { dealId, stageId, afterDealId }) {
    const all = Object.values(s.columns).reduce((acc, column) => acc.concat(column.items), []);
    const deal = all.find((item) => item.id === dealId);
    if (deal) {
      Object.keys(s.columns).forEach((id) => {
        const column = s.columns[id];
        let items = column.items.filter((item) => item.id !== dealId);
        if (id === stageId) {
          const at = afterDealId ? items.findIndex((item) => item.id === afterDealId) + 1 : 0;
          items = [...items.slice(0, at), { ...deal, stageId }, ...items.slice(at)];
        }
        if (items.length !== column.items.length || id === stageId) commit('SET_COLUMN', { stageId: id, items });
      });
    }
    return post('deal/move', { dealId, stageId, afterDealId: afterDealId || null })
      .finally(() => dispatch('fetchBoard'));
  },
  // items: [{ dealId, stageId?, delete? }]
  saveDealsBulk({ dispatch }, items) {
    return post('deal/save-bulk', { items }).then(() => dispatch('fetchBoard'));
  },
  // Everything under the current filters — not just the cards loaded on screen.
  exportDeals({ state: s }) {
    return axios.post(
      '/insurance/personal-pipeline/deal/download',
      { ownerUserId: s.ownerUserId, ...toRequestFilters(s.filters) },
      { responseType: 'blob' }
    )
      .then((resp) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
        link.download = 'Personal pipeline.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      });
  },
};

// Calls a detail page needs, independent of the board state.
export const fetchPersonalDeal = (dealId) => post('deal/detail', { dealId });
export const savePersonalDeal = (deal, ownerUserId = null) => post('deal/save', { ownerUserId, deal });
export const fetchPersonalDealActivities = (dealId) => post('deal/activity/list', { dealId });
export const savePersonalDealActivity = (body) => post('deal/activity/save', body);
export const fetchPersonalDealHistory = (dealId) => post('deal/history/list', { dealId });
// Deals for the task form's contact picker; the backend limits them to pipelines the user may open.
export const suggestPersonalDeals = (search) => post('deal/suggest', { search: search || null, limit: 50 })
  .then((data) => data.items);
export const archivePersonalDeal = (dealId) => post('deal/save-bulk', { items: [{ dealId, delete: true }] });

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
